import { useEffect, useState } from "react";
import { List } from "lucide-react";

interface Heading {
  id: string;
  text: string;
  level: number;
}

/**
 * Auto-generates a table of contents from h2/h3 inside the nearest <article>.
 * Sticky sidebar on desktop, collapsible <details> on mobile.
 */
export const ArticleToc = () => {
  const [headings, setHeadings] = useState<Heading[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);

  useEffect(() => {
    const article = document.querySelector("article");
    if (!article) return;
    const nodes = Array.from(article.querySelectorAll("h2, h3")) as HTMLHeadingElement[];
    const items: Heading[] = nodes.map((n, i) => {
      if (!n.id) {
        const slug = (n.textContent ?? `section-${i}`)
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/^-|-$/g, "")
          .slice(0, 60);
        n.id = slug || `section-${i}`;
      }
      return { id: n.id, text: n.textContent ?? "", level: n.tagName === "H2" ? 2 : 3 };
    });
    setHeadings(items);

    if (items.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActiveId(visible[0].target.id);
      },
      { rootMargin: "-80px 0px -70% 0px" },
    );
    nodes.forEach((n) => observer.observe(n));
    return () => observer.disconnect();
  }, []);

  if (headings.length < 3) return null;

  return (
    <>
      {/* Desktop sticky */}
      <aside
        aria-label="Table of contents"
        className="hidden xl:block fixed right-4 top-32 w-56 max-h-[70vh] overflow-y-auto z-30 2xl:w-60 2xl:right-8"
      >
        <p className="text-[10px] uppercase tracking-[0.22em] text-accent font-semibold mb-3 flex items-center gap-1.5">
          <List className="h-3 w-3" /> On this page
        </p>
        <ul className="space-y-1.5 border-l border-border/40 pl-3">
          {headings.map((h) => (
            <li key={h.id} className={h.level === 3 ? "pl-3" : ""}>
              <a
                href={`#${h.id}`}
                className={`block text-[12px] leading-snug transition-colors ${
                  activeId === h.id ? "text-accent font-medium" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {h.text}
              </a>
            </li>
          ))}
        </ul>
      </aside>

      {/* Mobile collapsible */}
      <details className="lg:hidden mb-8 rounded-md border border-border/40 bg-card/30 p-4">
        <summary className="cursor-pointer text-[11px] uppercase tracking-[0.22em] text-accent font-semibold flex items-center gap-1.5">
          <List className="h-3 w-3" /> Table of contents ({headings.length})
        </summary>
        <ul className="mt-3 space-y-1.5">
          {headings.map((h) => (
            <li key={h.id} className={h.level === 3 ? "pl-4" : ""}>
              <a href={`#${h.id}`} className="block text-sm text-muted-foreground hover:text-foreground">
                {h.text}
              </a>
            </li>
          ))}
        </ul>
      </details>
    </>
  );
};

export default ArticleToc;
